import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import useAccountDuplicates from "../../data/useAccountDuplicates";
import { useMergePreview } from "../../data/useContactMerges";
import { IContact, IMergePreviewContact } from "../../models/Contact";
import { addToMerge, contactMergeSteps, keepInstead, previewBankChoices } from "../../utils/contactMerges";
import useNotification from "../notifications/useNotifications";
import MergeColumn, { IMergeField, IMergeStat } from "../shared/Merge/MergeColumn";
import MergeLayout from "../shared/Merge/MergeLayout";
import BankDetailsChoice from "../shared/Merge/BankDetailsChoice";
import { SearchContact } from "./SearchContact";
import SharedContactLeadsTable from "./SharedContactLeadsTable";

interface ContactMergeModalProps {
  contact: IContact;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

// Merges other contacts into this one by hand - for anyone the Duplicates page cannot spot on its
// own, such as a nickname or a typo. Everyone in the merge is shown side by side, leads included,
// before anything moves; the merge can be undone from the kept contact's Merge history.
export function ContactMergeModal({ contact, open, setOpen }: ContactMergeModalProps) {
  const { t } = useTranslation("accounts");
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { mergeAccounts, mergeContacts } = useAccountDuplicates(false, "ContactMergeModal");
  const [keptId, setKeptId] = useState(contact.id);
  const [otherIds, setOtherIds] = useState<string[]>([]);
  const [bankFromId, setBankFromId] = useState<string | null>(null);
  const [isMerging, setIsMerging] = useState(false);

  const preview = useMergePreview(keptId, otherIds, "ContactMergeModal");
  const data = preview.data?.data;

  useEffect(() => {
    if (!open) {
      setKeptId(contact.id);
      setOtherIds([]);
      setBankFromId(null);
    }
  }, [open, contact.id]);

  const choices = data ? previewBankChoices(data) : [];
  const bankFrom = choices.find((c) => c.accountId === bankFromId) ?? choices[0] ?? null;
  const targetAccount = data?.accounts.find((a) => a.isTarget);
  const accountsMerged = data ? data.accounts.filter((a) => !a.isTarget) : [];
  const kept = data?.contacts.find((c) => c.isKept);

  const keep = (id: string) => {
    const next = keepInstead(keptId, otherIds, id);
    setKeptId(next.keptId);
    setOtherIds(next.otherIds);
  };

  const fields = (person: IMergePreviewContact): IMergeField[] => [
    { label: t("Email"), value: person.email },
    { label: t("Phone"), value: person.telephone1 },
    { label: t("Phone 2"), value: person.telephone2 },
    { label: t("Postcode"), value: person.postcode },
    { label: t("Account"), value: person.accountNumber ? `#${person.accountNumber}` : null },
  ];

  const stats = (person: IMergePreviewContact): IMergeStat[] => [
    { label: t("Leads"), count: person.leads.length },
    { label: t("Documents"), count: person.documents },
    { label: t("Messages"), count: person.messages },
    { label: t("Emails"), count: person.emails },
    { label: t("Orders"), count: person.orders },
  ];

  const moving: IMergeStat[] = data
    ? stats(data.contacts[0]).map((s, i) => ({
        label: s.label,
        count: data.contacts.filter((c) => !c.isKept).reduce((total, c) => total + stats(c)[i].count, 0),
      }))
    : [];

  const merge = async () => {
    if (!data || data.refusal) return;

    setIsMerging(true);
    try {
      const steps = contactMergeSteps(data, choices.length > 1 ? bankFrom?.accountId ?? null : null);

      if (steps.accounts) await mergeAccounts.mutateAsync(steps.accounts);
      if (steps.contacts) {
        const res = await mergeContacts.mutateAsync(steps.contacts);
        addNotification({ variant: "success", primaryText: res.message ?? t("Contacts merged") });
      }

      setOpen(false);

      // The contact this page was showing is now merged away: go to the one that was kept.
      if (keptId !== contact.id) navigate(`/contacts/${keptId}`);
    } catch (error: any) {
      addNotification({ variant: "error", primaryText: t("The merge did not finish"), secondaryText: error?.message });
    } finally {
      setIsMerging(false);
    }
  };

  const columns = (data?.contacts ?? []).map((person) => (
    <MergeColumn
      key={person.id}
      title={person.fullname || t("(no name)")}
      isKept={person.isKept}
      fields={fields(person)}
      keptFields={kept ? fields(kept) : []}
      stats={stats(person)}
      refusal={person.refusal}
      onRemove={person.isKept ? undefined : () => setOtherIds((current) => current.filter((x) => x !== person.id))}
      onKeep={person.isKept ? undefined : () => keep(person.id)}
    >
      {person.leads.length > 0 && <SharedContactLeadsTable leads={person.leads} variant="list" />}
    </MergeColumn>
  ));

  const notices =
    targetAccount && accountsMerged.length > 0 ? (
      <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
        <p className="font-medium">
          {t("These contacts are on different accounts. Account(s) {{from}} will be merged into #{{to}} first.", {
            from: accountsMerged.map((a) => `#${a.accountNumber}`).join(", "),
            to: targetAccount.accountNumber,
          })}
        </p>
        {accountsMerged
          .filter((a) => a.otherContacts > 0)
          .map((a) => (
            <p key={a.id}>
              {t("Account #{{from}} also has {{count}} other contact(s) - they move to account #{{to}}.", {
                from: a.accountNumber,
                count: a.otherContacts,
                to: targetAccount.accountNumber,
              })}
            </p>
          ))}
        {choices.length > 1 && <BankDetailsChoice choices={choices} selectedId={bankFrom?.accountId ?? null} onSelect={setBankFromId} />}
      </div>
    ) : null;

  return (
    <MergeLayout
      open={open}
      setOpen={setOpen}
      title={t("Merge contacts")}
      intro={t("Pick the contacts that are the same person. Everything on them moves onto the one kept, and the others are removed. Undo it any time from the kept contact's Merge history.")}
      search={
        <SearchContact
          label={t("Add a contact to the merge")}
          selectedContact={null}
          onContactSelect={(picked) => setOtherIds((current) => addToMerge(keptId, current, picked.id))}
        />
      }
      columns={columns}
      hasSelection={otherIds.length > 0}
      emptyText={t("Search for the contact to merge with this one. Both will appear side by side with their leads.")}
      isLoading={preview.isLoading}
      isError={preview.isError}
      notices={notices}
      moving={moving}
      keptName={kept?.fullname ?? undefined}
      fieldsFilled={data?.fieldsFilled}
      refusal={data?.refusal}
      confirmLabel={otherIds.length === 1 ? t("Merge 1 contact") : t("Merge {{count}} contacts", { count: otherIds.length })}
      confirmDisabled={!data || !!data.refusal || otherIds.length === 0 || preview.isFetching}
      isConfirming={isMerging}
      onConfirm={merge}
    />
  );
}
